/**
 * designer.js — Designer page utilities
 *
 * - White background removal (flood-fill from edges)
 * - Dominant colour extraction from uploaded images
 *
 * Usage:
 *   DesignerUtils.removeWhiteBg(imgElement, threshold)
 *     → returns a Promise<string> with a data URL of the cleaned image
 *
 *   DesignerUtils.extractColours(imgElement, count)
 *     → returns a Promise<string[]> with hex colour strings
 */

(function () {
  "use strict";


  /* ─────────────────────────────────────────────────────────
     WHITE BACKGROUND REMOVAL (flood-fill from corners)

     How it works:
     1. Draw the image onto a canvas
     2. Check if the image already has transparency (skip if so)
     3. Start flood-fill from all four corners
     4. Any white pixel (above threshold) connected to a corner
        is made transparent
     5. White pixels inside the logo are unreachable, so they stay

     Threshold: RGB channel minimum to count as "white".
     Default 240 means R, G, B each > 240.
  ──────────────────────────────────────────────────────────*/

  function removeWhiteBg(imageSrc, threshold) {
    threshold = threshold || 240;
    console.log("[removeWhiteBg] Called with src:", imageSrc);
    console.log("[removeWhiteBg] Threshold:", threshold);

    return new Promise(function (resolve, reject) {
      var img = new Image();
      img.crossOrigin = "Anonymous";
      img.src = imageSrc;

      img.onload = function () {
        console.log("[removeWhiteBg] Image loaded:", img.width, "x", img.height);

        var canvas = document.createElement("canvas");
        var ctx = canvas.getContext("2d");
        canvas.width = img.width;
        canvas.height = img.height;
        ctx.drawImage(img, 0, 0);

        var imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        var data = imageData.data;

        // Transparency guard
        if (data[3] === 0) {
          console.log("[removeWhiteBg] First pixel is transparent — skipping");
          resolve(imageSrc);
          return;
        }

        console.log("[removeWhiteBg] First pixel RGBA:", data[0], data[1], data[2], data[3]);

        var width = canvas.width;
        var height = canvas.height;
        var visited = new Uint8Array(width * height);
        var pixelsRemoved = 0;

        var queue = [
          [0, 0],
          [width - 1, 0],
          [0, height - 1],
          [width - 1, height - 1]
        ];

        console.log("[removeWhiteBg] Starting flood-fill from 4 corners...");

        while (queue.length > 0) {
          var point = queue.shift();
          var x = point[0];
          var y = point[1];
          var idx = y * width + x;

          if (x < 0 || x >= width || y < 0 || y >= height || visited[idx]) continue;

          visited[idx] = 1;

          var p = idx * 4;
          var r = data[p];
          var g = data[p + 1];
          var b = data[p + 2];

          if (r > threshold && g > threshold && b > threshold) {
            data[p + 3] = 0;
            pixelsRemoved++;
            queue.push([x + 1, y], [x - 1, y], [x, y + 1], [x, y - 1]);
          }
        }

        console.log("[removeWhiteBg] Pixels made transparent:", pixelsRemoved);

        ctx.putImageData(imageData, 0, 0);
        var resultUrl = canvas.toDataURL("image/png");
        console.log("[removeWhiteBg] Done. Result URL length:", resultUrl.length);
        resolve(resultUrl);
      };

      img.onerror = function (err) {
        console.error("[removeWhiteBg] Image failed to load:", imageSrc, err);
        reject(err);
      };
    });
  }


  /* ─────────────────────────────────────────────────────────
     DOMINANT COLOUR EXTRACTION

     How it works:
     1. Draw the image onto a small canvas (scaled down for speed)
     2. Sample all non-transparent pixels
     3. Group similar colours using simple bucketing
     4. Sort by frequency and return the top N as hex strings

     Returns colours sorted by dominance — [0] is most dominant.
     Useful for auto-populating the primary/secondary colour pickers.
  ──────────────────────────────────────────────────────────*/

  function extractColours(imgElement, count) {
    count = count || 3;
    var sampleSize = 100; // Scale down to 100px wide for speed

    return loadImageToCanvas(imgElement, sampleSize).then(function (result) {
      var canvas = result.canvas;
      var ctx    = result.ctx;
      var w      = canvas.width;
      var h      = canvas.height;
      var imageData = ctx.getImageData(0, 0, w, h);
      var data   = imageData.data;

      // Bucket colours: round each channel to nearest 16 for grouping
      var bucketSize = 64;
      var buckets = {};

      for (var i = 0; i < data.length; i += 4) {
        var r = data[i];
        var g = data[i + 1];
        var b = data[i + 2];
        var a = data[i + 3];

        // Skip transparent pixels
        if (a < 128) continue;

        // Skip white and light greys (lighter than 30% grey)
        // 30% grey ≈ RGB(178, 178, 178) — skip anything brighter
        if (r > 178 && g > 178 && b > 178) continue;

        // Bucket
        var br = Math.round(r / bucketSize) * bucketSize;
        var bg = Math.round(g / bucketSize) * bucketSize;
        var bb = Math.round(b / bucketSize) * bucketSize;
        var key = br + "," + bg + "," + bb;

        if (!buckets[key]) {
          buckets[key] = { r: 0, g: 0, b: 0, count: 0 };
        }
        buckets[key].r += r;
        buckets[key].g += g;
        buckets[key].b += b;
        buckets[key].count++;
      }

      // Sort buckets by frequency
      var sorted = Object.keys(buckets)
        .map(function (key) { return buckets[key]; })
        .sort(function (a, b) { return b.count - a.count; });

      // Return top N colours as hex strings (averaged within bucket)
      var colours = [];
      for (var j = 0; j < Math.min(count, sorted.length); j++) {
        var bucket = sorted[j];
        var avgR = Math.round(bucket.r / bucket.count);
        var avgG = Math.round(bucket.g / bucket.count);
        var avgB = Math.round(bucket.b / bucket.count);
        colours.push(rgbToHex(avgR, avgG, avgB));
      }

      return colours;
    });
  }


  /* ─────────────────────────────────────────────────────────
     HELPERS
  ──────────────────────────────────────────────────────────*/

  /**
   * Load an image element onto a canvas.
   * Handles CORS by creating a new Image with crossOrigin.
   *
   * @param {HTMLImageElement} imgElement
   * @param {number} [maxWidth]  Scale down to this width (preserves ratio)
   * @returns {Promise<{canvas, ctx}>}
   */
  function loadImageToCanvas(imgElement, maxWidth) {
    return new Promise(function (resolve, reject) {
      var img = new Image();
      img.crossOrigin = "anonymous";

      img.onload = function () {
        var w = img.naturalWidth;
        var h = img.naturalHeight;

        if (maxWidth && w > maxWidth) {
          var ratio = maxWidth / w;
          w = maxWidth;
          h = Math.round(h * ratio);
        }

        var canvas = document.createElement("canvas");
        canvas.width = w;
        canvas.height = h;
        var ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, w, h);

        resolve({ canvas: canvas, ctx: ctx });
      };

      img.onerror = function () {
        reject(new Error("Failed to load image: " + imgElement.src));
      };

      img.src = imgElement.src;
    });
  }

  function rgbToHex(r, g, b) {
    return "#" + [r, g, b].map(function (c) {
      var hex = c.toString(16);
      return hex.length === 1 ? "0" + hex : hex;
    }).join("");
  }


  /* ─────────────────────────────────────────────────────────
     CONTRAST TEXT COLOUR

     Given a background colour (hex string), returns "#000000"
     or "#ffffff" — whichever has higher contrast against it.

     Uses WCAG relative luminance formula:
       L = 0.2126 * R' + 0.7152 * G' + 0.0722 * B'
     where R' = (R/255)^2.2 (simplified gamma)

     Contrast ratio = (lighter + 0.05) / (darker + 0.05)
  ──────────────────────────────────────────────────────────*/

  function contrastTextColour(hexBg) {
    // Parse hex to RGB
    var hex = hexBg.replace("#", "");
    if (hex.length === 3) {
      hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
    }
    var r = parseInt(hex.substring(0, 2), 16) / 255;
    var g = parseInt(hex.substring(2, 4), 16) / 255;
    var b = parseInt(hex.substring(4, 6), 16) / 255;

    // Relative luminance (simplified sRGB)
    r = r <= 0.03928 ? r / 12.92 : Math.pow((r + 0.055) / 1.055, 2.4);
    g = g <= 0.03928 ? g / 12.92 : Math.pow((g + 0.055) / 1.055, 2.4);
    b = b <= 0.03928 ? b / 12.92 : Math.pow((b + 0.055) / 1.055, 2.4);

    var luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b;

    // Contrast ratio against white (L=1) vs black (L=0)
    var contrastWhite = (1.05) / (luminance + 0.05);
    var contrastBlack = (luminance + 0.05) / (0.05);

    return contrastWhite > contrastBlack ? "#ffffff" : "#000000";
  }


  /* ─────────────────────────────────────────────────────────
     SPORTS CONFIG LOADER

     Loads sports-config.json and provides helpers to read
     positions, stat layouts, handedness, and bio templates
     for any sport.
  ──────────────────────────────────────────────────────────*/

  var _sportsConfig = null;
  var _activeSport = null;

  function loadSportsConfig(url) {
    return fetch(url)
      .then(function (r) { return r.json(); })
      .then(function (config) {
        _sportsConfig = config;
        console.log('[designer.js] Sports config loaded:', Object.keys(config.sports).join(', '));
        return config;
      });
  }

  function setSport(sportId) {
    if (!_sportsConfig || !_sportsConfig.sports[sportId]) {
      console.warn('[designer.js] Unknown sport:', sportId);
      return null;
    }
    _activeSport = _sportsConfig.sports[sportId];
    console.log('[designer.js] Active sport:', sportId);
    return _activeSport;
  }

  function getSportConfig() {
    return _activeSport;
  }

  function getPositions() {
    return _activeSport ? _activeSport.positions : [];
  }

  function getHandedness(position) {
    if (!_activeSport) return { label: 'Shoots', options: ['Left', 'Right'] };
    var h = _activeSport.handedness;
    return (position && h[position]) || h['default'] || { label: 'Shoots', options: ['Left', 'Right'] };
  }

  function getStatLayout(position) {
    if (!_activeSport) return null;
    var layoutName = _activeSport.positionToLayout[position];
    return layoutName ? _activeSport.statLayouts[layoutName] : null;
  }

  function getStatLayoutName(position) {
    if (!_activeSport) return null;
    return _activeSport.positionToLayout[position] || null;
  }


  /* ─────────────────────────────────────────────────────────
     BIO GENERATOR

     Reads bio templates from the loaded sports config.
     Templates use tokens: {fn}, {team}, {town}, {pts}, {gp}, {goals}
  ──────────────────────────────────────────────────────────*/

  function generateBio(position, firstName, teamName, hometown, pts, gp, goals) {
    if (!_activeSport) return '';

    var templates = _activeSport.bioTemplates || {};
    var template = templates[position] || templates['_default'] || '';

    return template
      .replace(/\{fn\}/g,    firstName || 'Player')
      .replace(/\{team\}/g,  teamName || 'Team')
      .replace(/\{town\}/g,  hometown || 'Hometown')
      .replace(/\{pts\}/g,   pts || '0')
      .replace(/\{gp\}/g,    gp || '0')
      .replace(/\{goals\}/g, goals || '0');
  }


  /* ─────────────────────────────────────────────────────────
     PUBLIC API
  ──────────────────────────────────────────────────────────*/

  window.DesignerUtils = {
    removeWhiteBg:      removeWhiteBg,
    extractColours:     extractColours,
    contrastTextColour: contrastTextColour,
    loadSportsConfig:   loadSportsConfig,
    setSport:           setSport,
    getSportConfig:     getSportConfig,
    getPositions:       getPositions,
    getHandedness:      getHandedness,
    getStatLayout:      getStatLayout,
    getStatLayoutName:  getStatLayoutName,
    generateBio:        generateBio,
  };

}());
